import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { convertibleFromObject } from 'Modules/converter/index';
import { FitmentFieldValues, type ServerModel } from 'Modules/serverApi/types';

import { asCmError, GET, GETBACKUP } from './common/httpClient';
import { hasStringField, isArrayOf, isGenericItem, isObjectRecord } from './common/validators';
import { FITMENTS_ENDPOINT } from './endpoints';

const { requestDefaults } = requestConfig;

const errorFitmentsResponse: ServerModel.FitmentsResponse = {
  Fitment: FitmentFieldValues.NoFitmentData,
  Fields: [],
  Fitments: [],
};

export default async function fitments(productId: string): Promise<ServerModel.FitmentsResponse> {
  const extra = { ...requestDefaults.extra };
  const params = {
    productId,
    extra: await convertibleFromObject(extra),
  };

  try {
    return getFitmentsResponse(await GET(FITMENTS_ENDPOINT, params));
  } catch (primaryErr) {
    const pe = asCmError(primaryErr);

    if (pe.type === CustomErrorType.requestAborted) {
      return errorFitmentsResponse;
    }

    console.warn(`The fitments request failed: ${pe.message}`);

    if (pe.status === 429) {
      return errorFitmentsResponse;
    }

    try {
      const backupResponse = getFitmentsResponse(await GETBACKUP(FITMENTS_ENDPOINT, params));
      logNetworkRequest({ endpoint: FITMENTS_ENDPOINT, scenario: 'backup_used', primaryError: pe });
      return backupResponse;
    } catch (backupErr) {
      const be = asCmError(backupErr);
      console.warn(`The fitments backup request failed: ${be.message}`);
      logNetworkRequest({
        endpoint: FITMENTS_ENDPOINT,
        scenario: 'backup_failed',
        primaryError: pe,
        backupError: be,
      });
      return errorFitmentsResponse;
    }
  }
}

function getFitmentsResponse(value: unknown): ServerModel.FitmentsResponse {
  return isFitmentsResponse(value) ? value : errorFitmentsResponse;
}

function isFitmentsResponse(value: unknown): value is ServerModel.FitmentsResponse {
  return (
    isObjectRecord(value) &&
    isFitmentFieldValue(value.Fitment) &&
    isFitmentFields(value.Fields) &&
    isFitmentItems(value.Fitments)
  );
}

function isFitmentFieldValue(value: unknown): value is FitmentFieldValues {
  return Object.values(FitmentFieldValues).some((fieldValue) => fieldValue === value);
}

function isFitmentFields(value: unknown): value is ServerModel.FitmentsResponse['Fields'] {
  return isArrayOf(value, isFitmentField);
}

function isFitmentField(value: unknown): value is ServerModel.FitmentsResponse['Fields'][number] {
  return isObjectRecord(value) && hasStringField(value, 'Name') && hasStringField(value, 'Title');
}

function isFitmentItems(value: unknown): value is ServerModel.FitmentsResponse['Fitments'] {
  return isArrayOf(value, isGenericItem);
}
